import { Resources, Tools } from "@tsTypes/subject-schema";
import { BillingStatus, UserRole } from "../../enums/auth";
import { mapStripeStatus } from "../stripe/formatters";

export type AccessConfig =
  | "Full Access"
  | "Read Only"
  | "No Access"
  | "Membership Dependent";

export type UserAccess =
  | Extract<AccessConfig, "Full Access" | "Read Only" | "No Access">
  | "LOADING";

type RequireAll<T extends string | number | symbol> = {
  [K in T]: AccessConfig;
};

export type ResourceAccessConfig = RequireAll<"Unauthenticated"> &
  RequireAll<Exclude<UserRole, "Private">> & {
    ["Private"]: Record<
      ReturnType<typeof mapStripeStatus> | "none",
      | AccessConfig
      | Record<Extract<BillingStatus, "Paid" | "Failed">, AccessConfig>
    >;
  };

export type ToolAccessConfig = Record<
  "Unauthenticated" | UserRole,
  AccessConfig
>;

export const ALL_RESOURCE_ACCESS_CONFIG: Record<
  Resources,
  ResourceAccessConfig
> &
  Record<Tools, ToolAccessConfig> = {
  // ************************** RESOURCES **************************
  ["videos"]: {
    ["Unauthenticated"]: "No Access",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "Full Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: {
      ["none"]: "No Access",

      ["suspended"]: "No Access",
      ["expired"]: "No Access",
      ["trialing"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },
      ["active"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },
    },
  },
  ["notes"]: {
    ["Unauthenticated"]: "No Access",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "Full Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: {
      ["none"]: "No Access",
      ["trialing"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },
      ["active"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },

      ["suspended"]: "No Access",
      ["expired"]: "No Access",
    },
  },
  ["flashcards"]: {
    ["Unauthenticated"]: "No Access",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "Full Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: {
      ["none"]: "No Access",
      ["trialing"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },
      ["active"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },

      ["suspended"]: "No Access",
      ["expired"]: "No Access",
    },
  },
  ["questions"]: {
    ["Unauthenticated"]: "No Access",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "Full Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: {
      ["none"]: "No Access",
      ["trialing"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },
      ["active"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },

      ["suspended"]: "No Access",
      ["expired"]: "No Access",
    },
  },
  ["cram sheets"]: {
    ["Unauthenticated"]: "Read Only",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "Full Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: {
      ["none"]: "Read Only",
      ["trialing"]: {
        ["Paid"]: "Read Only",
        ["Failed"]: "No Access",
      },
      ["active"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },

      ["suspended"]: "Read Only",
      ["expired"]: "Read Only",
    },
  },
  ["definitions"]: {
    ["Unauthenticated"]: "Full Access",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "Full Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: {
      ["none"]: "Full Access",
      ["trialing"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },
      ["active"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },

      ["suspended"]: "Full Access",
      ["expired"]: "Full Access",
    },
  },
  ["command terms"]: {
    ["Unauthenticated"]: "Read Only",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "Full Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: {
      ["none"]: "Read Only",
      ["trialing"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },
      ["active"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },

      ["suspended"]: "Read Only",
      ["expired"]: "Read Only",
    },
  },
  ["exam tests"]: {
    ["Unauthenticated"]: "Read Only",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "Full Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: {
      ["none"]: "Read Only",
      ["trialing"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },
      ["active"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },

      ["suspended"]: "Read Only",
      ["expired"]: "Read Only",
    },
  },
  ["topic tests"]: {
    ["Unauthenticated"]: "Read Only",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "Full Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: {
      ["none"]: "Read Only",
      ["trialing"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },
      ["active"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },

      ["suspended"]: "Read Only",
      ["expired"]: "Read Only",
    },
  },
  ["individual topic test"]: {
    ["Unauthenticated"]: "No Access",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "Full Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: {
      ["none"]: "No Access",
      ["trialing"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },
      ["active"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },

      ["suspended"]: "No Access",
      ["expired"]: "No Access",
    },
  },
  ["mock exams"]: {
    ["Unauthenticated"]: "No Access",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "Full Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: {
      ["none"]: "No Access",
      ["trialing"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },
      ["active"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },

      ["suspended"]: "No Access",
      ["expired"]: "No Access",
    },
  },
  ["test builder"]: {
    ["Unauthenticated"]: "No Access",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "Full Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: {
      ["none"]: "No Access",
      ["trialing"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },
      ["active"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },

      ["suspended"]: "No Access",
      ["expired"]: "No Access",
    },
  },
  ["test taker"]: {
    ["Unauthenticated"]: "No Access",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "Full Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: {
      ["none"]: "No Access",
      ["trialing"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },
      ["active"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },

      ["suspended"]: "No Access",
      ["expired"]: "No Access",
    },
  },
  ["syllabus"]: {
    ["Unauthenticated"]: "Read Only",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "Full Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: {
      ["none"]: "Read Only",
      ["trialing"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },
      ["active"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },

      ["suspended"]: "Read Only",
      ["expired"]: "Read Only",
    },
  },
  ["exam breakdown"]: {
    ["Unauthenticated"]: "Read Only",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "Full Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: {
      ["none"]: "Read Only",
      ["trialing"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },
      ["active"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },

      ["suspended"]: "Read Only",
      ["expired"]: "Read Only",
    },
  },
  ["ia criteria"]: {
    ["Unauthenticated"]: "Read Only",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "Full Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: {
      ["none"]: "Read Only",
      ["trialing"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },
      ["active"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },

      ["suspended"]: "Read Only",
      ["expired"]: "Read Only",
    },
  },
  ["ia examples"]: {
    ["Unauthenticated"]: "Read Only",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "Full Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: {
      ["none"]: "Read Only",
      ["trialing"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },
      ["active"]: {
        ["Paid"]: "Membership Dependent",
        ["Failed"]: "No Access",
      },

      ["suspended"]: "Read Only",
      ["expired"]: "Read Only",
    },
  },
  // ************************** TOOLS **************************
  [Tools.ADMIN_TOOLS]: {
    ["Unauthenticated"]: "No Access",
    ["Super Admin"]: "Full Access",
    ["Admin"]: "Full Access",
    ["Content Creator"]: "No Access",
    ["Co-ordinator"]: "No Access",
    ["Teacher"]: "No Access",
    ["Student"]: "No Access",
    ["Private"]: "No Access",
  },
};
